import { error } from "console";
import { HOT } from "@hot-wallet/sdk";
import { AnchorProvider, BN, Program, Wallet, web3 } from "@coral-xyz/anchor";
import NodeWallet from "@coral-xyz/anchor/dist/cjs/nodewallet";
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  // Account,
  NATIVE_MINT,
  TOKEN_PROGRAM_ID,
  TokenAccountNotFoundError,
  TokenInvalidAccountOwnerError,
  createAssociatedTokenAccountIdempotentInstruction,
  createAssociatedTokenAccountInstruction,
  createCloseAccountInstruction,
  createSyncNativeInstruction,
  getAccount,
  getAssociatedTokenAddress,
  getAssociatedTokenAddressSync,
  // getOrCreateAssociatedTokenAccount,
} from "@solana/spl-token";
import {
  ComputeBudgetProgram,
  LAMPORTS_PER_SOL,
  PublicKey,
  SendTransactionError,
  SystemProgram,
} from "@solana/web3.js";
import Big from "big.js";

import { ToastService } from "@/components/ToastService/index.tsx";
import { TOAST_ID } from "@/constants/index.ts";
import { NetworkIconMap } from "@/constants/map.tsx";
import { formatTokenAmount } from "@/utils/amount.ts";
import { bufferFromString, calculateShouldReject } from "@/utils/index.ts";

import { HapiSolanaAttestation, IDL } from "./idl.tsx";

const SECP256K1_SIGNATURE_LENGTH = 64;

export class AttestationProgram {
  protected program: Program<HapiSolanaAttestation>;
  protected connection: web3.Connection;
  protected wallet: NodeWallet;

  constructor(connection: web3.Connection, wallet: Wallet, contractAddress: string) {
    const provider = new AnchorProvider(connection, wallet, {
      commitment: "confirmed",
    });
    this.program = new Program(IDL, new PublicKey(contractAddress), provider);
    this.wallet = wallet;
    this.connection = connection;
  }

  public findContractStateAddress(): [PublicKey, number] {
    return PublicKey.findProgramAddressSync([bufferFromString("state")], this.program.programId);
  }

  public findUserAddress(address: PublicKey): [PublicKey, number] {
    return PublicKey.findProgramAddressSync([bufferFromString("user"), address.toBytes()], this.program.programId);
  }

  public async getContractStateData() {
    const [state] = this.findContractStateAddress();

    return await this.program.account.state.fetch(state);
  }

  public async getUserData(address: PublicKey) {
    const [user] = this.findUserAddress(address);
    return await this.program.account.user.fetch(user);
  }

  public async checkATA(mint: PublicKey, owner: PublicKey, allowOwnerOffCurve = false) {
    const associatedToken = getAssociatedTokenAddressSync(
      mint,
      owner,
      allowOwnerOffCurve,
      TOKEN_PROGRAM_ID,
      ASSOCIATED_TOKEN_PROGRAM_ID,
    );
    let account;
    try {
      account = await getAccount(this.program.provider.connection, associatedToken);
    } catch (error: unknown) {
      return;
    }

    return account;
  }

  public async attestUser(
    verifying_signature: number[],
    recovery_id: number,
    score: number,
    expiration_date: BN,
    isUpdateMint?: boolean,
  ) {
    const sender = this.program.provider.publicKey;
    if (!sender) return;
    let solanaWrapInstructions: web3.TransactionInstruction[] = [];
    let solanaCloseAccountInstructions: web3.TransactionInstruction[] = [];
    const feeAccountInstructions: web3.TransactionInstruction[] = [];

    const icon = NetworkIconMap.SOLANA;
    const [user, bump] = this.findUserAddress(sender);
    const [state] = this.findContractStateAddress();

    if (verifying_signature.length != SECP256K1_SIGNATURE_LENGTH) {
      throw Error("Invalid signature");
    }

    const state_data = await this.getContractStateData();
    const balance = await this.connection.getBalance(new PublicKey(sender));

    const feeTokenAccount = getAssociatedTokenAddressSync(
      state_data.feeMint,
      state_data.authority,
      true,
      TOKEN_PROGRAM_ID,
      ASSOCIATED_TOKEN_PROGRAM_ID,
    );
    const feeTokenAccountInfo = await this.checkATA(state_data.feeMint, state_data.authority, true);
    if (!feeTokenAccountInfo) {
      // The fee collector's associated token account (wSOL) may not exist on-chain,
      // in which case Anchor rejects the call with "feeTokenAccount not provided".
      // Create it idempotently so `attestUser` always receives a valid fee account.
      feeAccountInstructions.push(
        createAssociatedTokenAccountIdempotentInstruction(
          sender,
          feeTokenAccount,
          state_data.authority,
          state_data.feeMint,
          TOKEN_PROGRAM_ID,
          ASSOCIATED_TOKEN_PROGRAM_ID,
        ),
      );
    }
    const feeUserTokenAccount = await this.checkATA(state_data.feeMint, sender);

    const tokenBalance = feeUserTokenAccount
      ? await this.connection.getTokenAccountBalance(new PublicKey(feeUserTokenAccount.address))
      : 0;

    const feeAmount = isUpdateMint ? state_data.updateAttestationFee : state_data.createAttestationFee;
    if (!feeUserTokenAccount || Big(tokenBalance ? tokenBalance?.value?.amount : 0).lt(feeAmount)) {
      if (Big(balance).gte(feeAmount)) {
        const associatedTokenAccount = await getAssociatedTokenAddress(NATIVE_MINT, sender);
        const amount = Big(formatTokenAmount(feeAmount, 9)).mul(LAMPORTS_PER_SOL).toNumber();
        let createdAssociatedTokenAccountInstruction: web3.TransactionInstruction[] = [];

        try {
          await getAccount(this.connection, associatedTokenAccount);
        } catch (error: unknown) {
          if (error instanceof TokenAccountNotFoundError || error instanceof TokenInvalidAccountOwnerError) {
            createdAssociatedTokenAccountInstruction = [
              createAssociatedTokenAccountInstruction(sender, associatedTokenAccount, sender, NATIVE_MINT),
            ];
          } else {
            throw error;
          }
        }
        const wrapTransactionsInstructions = [
          ...createdAssociatedTokenAccountInstruction,
          SystemProgram.transfer({
            fromPubkey: sender,
            toPubkey: associatedTokenAccount,
            lamports: amount,
          }),
          createSyncNativeInstruction(associatedTokenAccount),
        ];

        solanaWrapInstructions = wrapTransactionsInstructions;
        solanaCloseAccountInstructions = [createCloseAccountInstruction(associatedTokenAccount, sender, sender)];
      } else {
        ToastService.simple({ text: "Top up SOL balance", id: TOAST_ID.MINT, icon });
        throw error("Inefficient SOL balance");
      }
    }
    const senderTokenAccount = await getAssociatedTokenAddress(state_data.feeMint, sender);

    const attestTransactionInstruction = await this.program.methods
      .attestUser(verifying_signature, recovery_id, score, expiration_date, bump)
      .accounts({
        sender,
        user,
        state,
        feeTokenAccount,
        senderTokenAccount,
        tokenProgram: TOKEN_PROGRAM_ID,
        systemProgram: web3.SystemProgram.programId,
      })
      .instruction();

    let addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: 10000,
    });

    ToastService.loading({ id: TOAST_ID.AIRDROPS, text: "Creating attestation" });

    let messageV0 = new web3.TransactionMessage({
      payerKey: sender,
      recentBlockhash: (await this.connection.getLatestBlockhash()).blockhash,
      instructions: [
        ...feeAccountInstructions,
        ...solanaWrapInstructions,
        attestTransactionInstruction,
        ...solanaCloseAccountInstructions,
      ],
    }).compileToV0Message();

    let transaction = new web3.VersionedTransaction(messageV0);

    const response = await fetch("https://mainnet.helius-rpc.com/?api-key=e5134d0c-9f20-48b6-ada5-33583b7f78fc", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: "helius-example",
        method: "getPriorityFeeEstimate",
        params: [
          {
            accountKeys: [TOKEN_PROGRAM_ID, state_data.feeMint, state_data.authority],
            options: { includeAllPriorityFeeLevels: true, includeVote: false },
          },
        ],
      }),
    });

    const result = await response.json();

    const priorityFee = result.result.priorityFeeLevels.low || 200000;

    const shouldReject = calculateShouldReject(result.result.priorityFeeEstimate, feeAmount);
    if (shouldReject) {
      ToastService.update({ id: TOAST_ID.AIRDROPS, text: "Gas fee too high, retry later" });
      return false;
    }

    addPriorityFee = ComputeBudgetProgram.setComputeUnitPrice({
      microLamports: priorityFee || 200000,
    });

    ToastService.loading({ id: TOAST_ID.AIRDROPS, text: "Creating attestation" });

    messageV0 = new web3.TransactionMessage({
      payerKey: sender,
      recentBlockhash: (await this.connection.getLatestBlockhash()).blockhash,
      instructions: [
        addPriorityFee,
        ...feeAccountInstructions,
        ...solanaWrapInstructions,
        attestTransactionInstruction,
        ...solanaCloseAccountInstructions,
      ],
    }).compileToV0Message();

    transaction = new web3.VersionedTransaction(messageV0);

    if (!this.program.provider.sendAndConfirm) {
      return false;
    }
    const latestBlockHash = await this.connection.getLatestBlockhash();

    try {
      const tx = await HOT.request("solana:signAndSendTransaction", { transaction: Buffer.from(transaction.serialize()).toString('base64'), sendOptions: { maxRetries: 3 } });
      // const tx = await this.program.provider.sendAndConfirm(transaction, undefined, { maxRetries: 3 }); TEMP FIX WHILE HOT IS BROKEN
      const signature = tx.signature;
      await this.connection.confirmTransaction({
        blockhash: latestBlockHash.blockhash,
        lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
        signature: signature,
      });

      ToastService.update({ id: TOAST_ID.AIRDROPS, text: "Created" });
      return true;
    } catch (e: unknown) {

      if (typeof e === "object" && e instanceof SendTransactionError) {
        console.log(e);
        if (
          e.transactionError.message.includes("insufficient") ||
          e.transactionError.logs?.some((message) => message.split(" ").includes("insufficient"))
        ) {
          ToastService.update({ id: TOAST_ID.AIRDROPS, text: "Top up SOL balance and try again" });
          return false;
        }
      }
      if (typeof e === "string") {
        console.log("err", (e as string).split(" "), e);
        const tx = (e as string).split(" ");
        if (tx[7]) {
          await this.connection.confirmTransaction({
            blockhash: latestBlockHash.blockhash,
            lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
            signature: tx[7],
          });

          ToastService.update({ id: TOAST_ID.AIRDROPS, text: "Created" });
          return true;
        }
      }

      return false;
    } finally {
      ToastService.clear(TOAST_ID.AIRDROPS);
    }
  }
}
